/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { EventType, RelationType } from '../../@types/event';
import type { XHubChatClient } from '../../client';
import type { XHubChatEvent } from '../event/event';
import type { EventTimelineSet } from '../event/event-timeline-set';
import type { Room } from '../room/room';
import { EventStatus } from '../../@types/event';
import { Relations } from './relations';

export class RelationsContainer {
  private relations = new Map<string, Map<RelationType | string, Map<EventType | string, Relations>>>();

  public constructor(
    private readonly client: XHubChatClient,
    private readonly room?: Room,
  ) {}

  /**
   * Add relation events to the relevant relation collection.
   * @param event - The new child event to be aggregated.
   * @param timelineSet - The event timeline set within which to search for the related event if any.
   */
  public aggregateChildEvent(event: XHubChatEvent, timelineSet?: EventTimelineSet): void {
    if (event.status === EventStatus.CANCELLED) return;

    const relation = event.getRelation();
    if (!relation) return;

    const { event_id: relatesToEventId, rel_type: relationType } = relation;
    const eventType = event.getType();

    let relationsForEvent = this.relations.get(relatesToEventId!);
    if (!relationsForEvent) {
      relationsForEvent = new Map<RelationType | string, Map<EventType | string, Relations>>();
      this.relations.set(relatesToEventId!, relationsForEvent);
    }

    let relationsWithRelType = relationsForEvent.get(relationType!);
    if (!relationsWithRelType) {
      relationsWithRelType = new Map<EventType | string, Relations>();
      relationsForEvent.set(relationType!, relationsWithRelType);
    }

    let relationsWithEventType = relationsWithRelType.get(eventType);
    if (!relationsWithEventType) {
      relationsWithEventType = new Relations(relationType!, eventType, this.client);
      relationsWithRelType.set(eventType, relationsWithEventType);

      const room = this.room ?? timelineSet?.room;
      const relatesToEvent
        = timelineSet?.findEventById(relatesToEventId!)
          ?? room?.findEventById(relatesToEventId!)
          ?? room?.getPendingEvent(relatesToEventId!);
      if (relatesToEvent) {
        relationsWithEventType.setTargetEvent(relatesToEvent);
      }
    }

    relationsWithEventType.addEvent(event);
  }

  public getAllChildEventsForEvent(parentEventId: string): XHubChatEvent[] {
    const relationsForEvent
      = this.relations.get(parentEventId) ?? new Map<RelationType | string, Map<EventType | string, Relations>>();
    const events: XHubChatEvent[] = [];
    for (const relationsRecord of relationsForEvent.values()) {
      for (const relations of relationsRecord.values()) {
        events.push(...relations.getRelations());
      }
    }
    return events;
  }

  /**
   * Get a collection of child events to a given event in this timeline set.
   *
   * @param eventId - The ID of the event that you'd like to access child events for.
   * For example, with annotations, this would be the ID of the event being annotated.
   * @param relationType - The type of relationship involved, such as "m.annotation", "m.reference", "m.replace", etc.
   * @param eventType - The relation event's type, such as "m.reaction", etc.
   * @throws If `eventId</code>, <code>relationType</code> or <code>eventType`
   * are not valid.
   *
   * @returns
   * A container for relation events or undefined if there are no relation events for
   * the relationType.
   */
  public getChildEventsForEvent(
    eventId: string,
    relationType: RelationType | string,
    eventType: EventType | string,
  ): Relations | undefined {
    return this.relations.get(eventId)?.get(relationType)?.get(eventType);
  }

  /**
   * Set an event as the target event if any Relations exist for it already.
   * Child events can point to other child events as their parent, so this method may be
   * called for events which are also logically child events.
   *
   * @param event - The event to check as relation target.
   */
  public aggregateParentEvent(event: XHubChatEvent): void {
    const relationsForEvent = this.relations.get(event.getId()!);
    if (!relationsForEvent) return;

    for (const relationsWithRelType of relationsForEvent.values()) {
      for (const relationsWithEventType of relationsWithRelType.values()) {
        relationsWithEventType.setTargetEvent(event);
      }
    }
  }
}
